import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader2, Save, Check } from 'lucide-react';

interface PageSection {
  id: number;
  section_key: string;
  content: string;
  content_type: string;
  sort_order: number;
  updated_at: string;
}

const PAGE_OPTIONS = [
  { value: 'home', label: 'Home Page' },
  { value: 'about', label: 'About Page' },
  { value: 'founder', label: 'Founder Page' },
  { value: 'terms', label: 'Terms Page' },
];

export const PageEditor = () => {
  const [selectedPage, setSelectedPage] = useState('home');
  const [sections, setSections] = useState<PageSection[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (selectedPage) fetchSections();
  }, [selectedPage]);

  const fetchSections = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.get<PageSection[]>(`/pages/admin/${selectedPage}`);
      setSections(data || []);
    } catch (err) {
      setError('Failed to load page content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSaved(false);

      const sectionsMap: Record<string, { content: string; content_type: string }> = {};
      for (const s of sections) {
        sectionsMap[s.section_key] = { content: s.content, content_type: s.content_type };
      }

      await api.put(`/pages/admin/${selectedPage}`, { sections: sectionsMap });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (index: number, content: string) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], content };
    setSections(updated);
  };

  const addSection = () => {
    const key = `section_${Date.now()}`;
    setSections([...sections, {
      id: 0,
      section_key: key,
      content: '',
      content_type: 'html',
      sort_order: sections.length,
      updated_at: new Date().toISOString(),
    }]);
  };

  const removeSection = (index: number) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#f5f5f5]">Page Editor</h1>
        <div className="flex items-center gap-3">
          <select
            value={selectedPage}
            onChange={e => setSelectedPage(e.target.value)}
            className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-[#f5f5f5] text-sm"
          >
            {PAGE_OPTIONS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <Button onClick={handleSave} disabled={saving} className="bg-[#E8651A] hover:bg-[#C9520E] text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : saved ? <Check className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {saved ? 'Saved!' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-800 text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E8651A]" />
        </div>
      ) : (
        <div className="space-y-4">
          {sections.length === 0 ? (
            <div className="text-center text-[#666] py-12 bg-[#111] border border-[#222] rounded-lg">
              No content sections for this page yet.
            </div>
          ) : (
            sections.map((section, i) => (
              <Card key={section.section_key} className="border-[#333] bg-[#111]">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm text-[#888] font-mono">
                    {section.section_key}
                  </CardTitle>
                  <button
                    onClick={() => removeSection(i)}
                    className="text-xs text-[#666] hover:text-red-400 transition-colors"
                  >
                    Remove
                  </button>
                </CardHeader>
                <CardContent>
                  <textarea
                    value={section.content}
                    onChange={e => updateSection(i, e.target.value)}
                    className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-2 text-[#f5f5f5] text-sm font-mono min-h-[120px]"
                    placeholder="HTML content for this section..."
                  />
                </CardContent>
              </Card>
            ))
          )}

          <Button onClick={addSection} variant="outline" className="w-full">
            + Add Section
          </Button>
        </div>
      )}
    </div>
  );
};
